import { Member } from '@/types/member';
import { supabase } from '@/integrations/supabase/client';

interface CRMEnrichmentData {
  lead_score: number;
  lead_source: string;
  lead_status: string;
  lifetime_value: number;
  last_interaction?: string;
  referral_source?: string;
}

export class CRMEnrichmentService {
  /**
   * Calcule le score de lead basé sur les données du membre
   */
  static calculateLeadScore(member: Member): number {
    let score = 0;
    
    // Complétude du profil (max 25 points)
    const profileFields = [member.name, member.email, member.company, member.role, member.phone, member.city];
    const completedFields = profileFields.filter(field => field && field.trim() !== '').length;
    score += Math.round((completedFields / profileFields.length) * 25);
    
    // Type de souscription (max 20 points)
    if (member.subscription_type === 'premium') score += 20;
    else if (member.subscription_type === 'standard') score += 15;
    else if (member.subscription_type === 'basic') score += 10;
    
    // Statut actif (max 15 points)
    if (member.status === 'actif') score += 15;
    
    // Taille d'entreprise basée sur le type (max 15 points)
    if (member.type === 'corporate') score += 15;
    else if (member.type === 'entrepreneur') score += 12;
    else if (member.type === 'freelance') score += 8;
    
    // Décisionnaire (max 10 points)
    if (member.decision_maker) score += 10;
    
    // Présence sur les réseaux sociaux (max 10 points)
    if (member.linkedin) score += 5;
    if (member.website) score += 3;
    if (member.twitter) score += 2;
    
    // Compétences et besoins définis (max 5 points)
    if (member.skills && member.skills.length > 0) score += 3;
    if (member.needs && member.needs.length > 0) score += 2;
    
    return Math.min(100, Math.max(0, score));
  }

  /**
   * Détermine la source de lead
   */
  static determineLeadSource(member: Member): string {
    if (member.lead_source) return member.lead_source;
    if (member.referral_source) return `Parrainage: ${member.referral_source}`;
    
    // Logique d'inférence basée sur les données disponibles
    if (member.linkedin && !member.website) return 'LinkedIn';
    if (member.website && !member.linkedin) return 'Site web';
    if (member.email?.includes('@gmail.com') || member.email?.includes('@outlook.com')) {
      return 'Inscription directe';
    }
    
    return 'Inscription directe';
  }

  /**
   * Détermine le statut de lead basé sur les données
   */
  static determineLeadStatus(member: Member): string {
    if (member.lead_status) return member.lead_status;
    
    const score = this.calculateLeadScore(member);
    const daysSinceJoin = member.join_date ? 
      Math.floor((Date.now() - new Date(member.join_date).getTime()) / (1000 * 60 * 60 * 24)) : 0;
    
    if (score >= 80 && member.status === 'actif') return 'qualified';
    if (score >= 60 && daysSinceJoin <= 30) return 'contacted';
    if (score >= 40) return 'new';
    
    return 'new';
  }

  /**
   * Calcule la valeur à vie basée sur le type d'abonnement
   */
  static calculateLifetimeValue(member: Member): number {
    if (member.lifetime_value && member.lifetime_value > 0) {
      return member.lifetime_value;
    }
    
    // Valeurs basées sur le type d'abonnement (estimation annuelle)
    const subscriptionValues: Record<string, number> = {
      'premium': 1200,
      'standard': 600,
      'basic': 300,
      'free': 0
    };
    
    const baseValue = subscriptionValues[member.subscription_type] || 300;
    
    // Multiplicateur basé sur le type de membre
    let multiplier = 1;
    if (member.type === 'corporate') multiplier = 2;
    else if (member.type === 'entrepreneur') multiplier = 1.5;
    
    // Bonus pour les décisionnaires
    if (member.decision_maker) multiplier *= 1.3;
    
    return Math.round(baseValue * multiplier);
  }

  /**
   * Enrichit un membre avec les données CRM calculées
   */
  static enrichMember(member: Member): Member {
    const enrichedData: CRMEnrichmentData = {
      lead_score: this.calculateLeadScore(member),
      lead_source: this.determineLeadSource(member),
      lead_status: this.determineLeadStatus(member),
      lifetime_value: this.calculateLifetimeValue(member),
      last_interaction: member.last_interaction || member.created_at || new Date().toISOString(),
      referral_source: member.referral_source || undefined
    };

    return {
      ...member,
      ...enrichedData
    };
  }

  /**
   * Enrichit une liste de membres
   */
  static enrichMembers(members: Member[]): Member[] {
    return members.map(member => this.enrichMember(member));
  }

  /**
   * Met à jour les données CRM dans la base de données pour un membre
   */
  static async updateMemberCRMData(memberId: string, member: Member): Promise<void> {
    const enrichmentData = {
      lead_score: this.calculateLeadScore(member),
      lead_source: this.determineLeadSource(member),
      lead_status: this.determineLeadStatus(member),
      lifetime_value: this.calculateLifetimeValue(member),
      last_interaction: member.last_interaction || member.created_at || new Date().toISOString()
    };

    const { error } = await supabase
      .from('club_members')
      .update(enrichmentData)
      .eq('id', memberId);

    if (error) {
      console.error('Erreur lors de la mise à jour des données CRM:', error);
      throw error;
    }
  }

  /**
   * Lance l'enrichissement pour tous les membres existants
   */
  static async enrichAllMembers(): Promise<void> {
    try {
      // Récupérer tous les membres
      const { data: members, error: fetchError } = await supabase
        .from('club_members')
        .select('*');

      if (fetchError) throw fetchError;
      if (!members) return;

      // Enrichir et mettre à jour chaque membre
      for (const member of members) {
        const enrichmentData = {
          lead_score: this.calculateLeadScore(member as Member),
          lead_source: this.determineLeadSource(member as Member),
          lead_status: this.determineLeadStatus(member as Member),
          lifetime_value: this.calculateLifetimeValue(member as Member),
          last_interaction: member.last_interaction || member.created_at || new Date().toISOString()
        };

        const { error: updateError } = await supabase
          .from('club_members')
          .update(enrichmentData)
          .eq('id', member.id);

        if (updateError) {
          console.error(`Erreur lors de l'enrichissement du membre ${member.id}:`, updateError);
        }
      }

      console.log(`Enrichissement terminé pour ${members.length} membres`);
    } catch (error) {
      console.error('Erreur lors de l\'enrichissement des membres:', error);
      throw error;
    }
  }
}